import { useEffect, useMemo, type VoidFunctionComponent } from 'react';
import type { InputActionMeta } from 'react-select';

import { useSearchPackageVersions } from '../../../hooks/api/registrySearch/useSearchPackageVersions';
import { useDebouncedValue } from '../../../hooks/useDebouncedValue';
import FormLabel from '../../generic/Forms/FormLabel';
import { Select } from '../../generic/Select';
import type { ThirdPartyFetcher } from '../../../../shared/fetchers';
import type { VersionHit, VersionKind } from '../../../../shared/registrySearch';

import { NO_DROPDOWN_COMPONENTS } from './selectIndicators';

import styles from './PackageAutocomplete.module.less';

const COMMIT_DISPLAY_CHARS = 12;

type VersionOption = {
  value: string;
  label: string;
  releaseDate?: string;
  kind?: VersionKind;
  fromTypeahead: boolean;
};

function formatReleaseDate(iso?: string): string | undefined {
  if (!iso) {
    return undefined;
  }
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? undefined : d.toLocaleDateString();
}

// Extracted to module scope (instead of inlining in JSX) to avoid `react/no-unstable-nested-components`.
// Used in the dropdown menu only — controlled-input mode renders the selected version directly
// in the input, not via this formatter.
const formatOptionLabel = (opt: VersionOption) => {
  // Commit SHAs are ugly at full length in a dropdown row; truncate for display only. The
  // option's `value` (and the value emitted on pick) stays the full 40-char SHA.
  const displayLabel = opt.kind === 'commit' && opt.label.length > COMMIT_DISPLAY_CHARS
    ? `${opt.label.slice(0, COMMIT_DISPLAY_CHARS)}…`
    : opt.label;
  const secondary = opt.kind ?? formatReleaseDate(opt.releaseDate);
  return (
    <div className={styles.optionRow}>
      <div className={styles.optionLabel}>{displayLabel}</div>
      {secondary && <div className={styles.optionSecondary}>{secondary}</div>}
    </div>
  );
};

type Props = {
  id: string;
  fetcher: ThirdPartyFetcher;
  packageName: string;
  value: string;
  /**
   * Emitted with the selected version and a `fromTypeahead` flag indicating whether the value
   * came from the prepopulated list (true) or was typed/created by the user (false).
   */
  onChange: (version: string, fromTypeahead: boolean) => void;
  /**
   * Fired when the field loses focus. The parent uses this to "commit" the current input as the
   * version, so a architecture lookup doesn't fire for every partial keystroke.
   */
  onBlur?: () => void;
  /**
   * Fired whenever a version list finishes loading for the current package, with the raw
   * version strings. The parent uses this to revalidate a version preserved across a package
   * change (keeping it only if it still exists in the new package's list)
   */
  onVersionsLoaded?: (versions: string[]) => void;
  /**
   * Whether the typeahead dropdown should engage. When false, the field still accepts typed
   * input (manual entry) but skips the version-list fetch and keeps the dropdown closed —
   * used when the package shape isn't one our backend can autocomplete.
   */
  isMenuEnabled?: boolean;
  isDisabled?: boolean;
  isRequired?: boolean;
};

function compareByReleaseDateDesc(a: VersionHit, b: VersionHit): number {
  if (a.releaseDate && b.releaseDate) {
    return b.releaseDate.localeCompare(a.releaseDate);
  }
  if (a.releaseDate) {
    return -1;
  }
  if (b.releaseDate) {
    return 1;
  }
  return 0;
}

export const PackageVersionAutocomplete: VoidFunctionComponent<Props> = ({
  id, fetcher, packageName, value, onChange, onBlur, onVersionsLoaded, isMenuEnabled = true, isDisabled, isRequired,
}) => {
  // Debounce the typed value so per-keystroke typing doesn't spam the registry-search
  // backend; we use the debounced query to drive the version-list fetch.
  const { debouncedValue: debouncedQuery } = useDebouncedValue(value);

  const { data, isLoading } = useSearchPackageVersions(fetcher, packageName, debouncedQuery, isMenuEnabled);

  // `data` is never a previous package's list here, so on a packageName change it goes undefined until the new package's list arrives.
  useEffect(() => {
    if (data?.results) {
      onVersionsLoaded?.(data.results.map((hit) => hit.value));
    }
  }, [data, onVersionsLoaded]);

  const options = useMemo<VersionOption[]>(() => {
    const hits = [...(data?.results ?? [])].sort(compareByReleaseDateDesc);
    return hits.map((hit) => ({
      value: hit.value,
      label: hit.value,
      releaseDate: hit.releaseDate,
      kind: hit.kind,
      fromTypeahead: true,
    }));
  }, [data]);

  return (
    <FormLabel label="Version" htmlFor={`${id}-input`} requiredText={isRequired ? '(Required)' : undefined}>
      <Select<VersionOption, false>
        id={id}
        inputId={`${id}-input`}
        className={styles.alwaysVisibleInput}
        // Keep react-select's internal selected-option state always null. We use this as a
        // controlled-input typeahead (we own `inputValue` and bubble picks via `onChange`); we
        // never read react-select's `value` ourselves, so letting it accumulate a stale
        // selection would only be a source of subtle bugs (e.g. previously-picked option
        // re-highlighting on menu reopen, or keyboard nav landing on a stale row).
        value={null}
        // Controlled-input mode (matches PackageNameAutocomplete) — renders the selected/typed
        // version as plain editable text, not a non-editable chip.
        inputValue={value}
        controlShouldRenderValue={false}
        // Force the menu closed in manual-entry mode (unsupported package shape) and strip the
        // dropdown indicators so the field reads as a plain input.
        menuIsOpen={isMenuEnabled ? undefined : false}
        components={isMenuEnabled ? undefined : NO_DROPDOWN_COMPONENTS}
        options={options}
        // Server-side narrowing: the typed value is debounced and forwarded to
        // `searchPackageVersions` as `query`, which the registry impl applies upstream-natively
        // (GitHub matching-refs prefix) or in-memory (npm). Disable react-select's default
        // substring filter so we display the server's result set unaltered — the server
        // already chose what matches.
        filterOption={null}
        onBlur={onBlur}
        onInputChange={(input: string, meta: InputActionMeta) => {
          if (meta.action === 'input-change') {
            onChange(input, false);
          }
        }}
        onChange={(opt) => {
          if (opt) {
            onChange(opt.value, true);
          }
        }}
        formatOptionLabel={formatOptionLabel}
        placeholder={packageName ? 'Select a version' : 'Pick a package first'}
        isDisabled={isDisabled || !packageName}
        // Gate the spinner on `isMenuEnabled` — react-query's `isLoading` can stay truthy
        // briefly during queryKey transitions even when `enabled` flips false, which would
        // show a misleading throbber while typing a value our backend won't search.
        isLoading={isMenuEnabled && isLoading}
        noOptionsMessage={() => (packageName ? 'No versions found' : 'Pick a package first')}
      />
    </FormLabel>
  );
};
